import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js';
import './styles.css';
import type { JointSpec, SceneSpecV1, Vec3 } from './contracts/scene-spec.js';
import type { CompiledSceneRuntime } from './compiler/compile-scene-spec.js';
import { validateSceneSpec } from './compiler/validate-scene-spec.js';

const WATCH_PROMPT =
  '参考 GW-M5610U-1 的官方正面、微距、侧面、四分之三视角、表背、表扣与照明图片，呈现小红圈腕表的经典方形轮廓、树脂表带、金属后盖和层次丰富的表盘细节。';
const FAN_PROMPT =
  '设计一个石墨黑、橙色机械细节与薄荷色七叶转子的模块化桌面循环扇，能够调速、摇头和俯仰。';

interface ProductPageConfig {
  path: '/watch/' | '/motorcycle/';
  ariaLabel: string;
  viewportBadge: string;
  eyebrow: string;
  modelChip: string;
  brand: string;
  title: string;
  prompt: string;
  seed: number;
  features: readonly string[];
  referenceTitle: string;
  referenceCount: string;
  referenceLabels: readonly string[];
  referenceImages?: readonly {
    src: string;
    thumbnailSrc: string;
    alt: string;
    caption: string;
  }[];
  referenceCopy: string;
  sourceLinks: readonly { label: string; href: string }[];
  metrics: readonly { label: string; value: string }[];
  footer: string;
  detailViewLabel: string;
}

const watchPage: ProductPageConfig = {
  path: '/watch/',
  ariaLabel: '手表互动展示',
  viewportBadge: '七视图造型参考 · 互动拆解',
  eyebrow: '经典方块腕表 · 小红圈赏析',
  modelChip: '3495',
  brand: 'G‑SHOCK',
  title: 'GW‑M5610U‑1',
  prompt: WATCH_PROMPT,
  seed: 5610,
  features: ['六局电波校时', '太阳能动力', '20 巴防水'],
  referenceTitle: '造型参考',
  referenceCount: '7 张参考',
  referenceLabels: ['正面', '微距', '侧面', '四分之三', '背面', '表扣', '照明'],
  referenceCopy:
    '依据官方产品图片观察腕表的整体比例、表壳厚度、按钮位置、表带弧度与背盖细节；页面不展示或再分发原始图片。',
  sourceLinks: [
    {
      label: '官方产品页',
      href: 'https://www.casio.com/ca-en/watches/gshock/product.GW-M5610U-1/',
    },
    {
      label: '使用说明书',
      href: 'https://www.casio.com/content/dam/casio/global/support/manuals/watches/pdf/34/3495/qw3495_EN.pdf',
    },
  ],
  metrics: [
    { label: '动态时间', value: '本地同步' },
    { label: '观察视角', value: '5 种' },
    { label: '部件查看', value: '可高亮' },
    { label: '拆解展示', value: '可调节' },
  ],
  footer:
    '本页面为非官方腕表外观赏析，与卡西欧及 G‑SHOCK 品牌无隶属关系。产品名称与标识归其权利人所有。',
  detailViewLabel: '表盘微距',
};

const motorcyclePage: ProductPageConfig = {
  path: '/motorcycle/',
  ariaLabel: '摩托车互动展示',
  viewportBadge: '现代复古街车 · 互动拆解',
  eyebrow: '机械结构与骑行姿态研究',
  modelChip: 'ROAD',
  brand: '现代复古街车',
  title: '城市机械',
  prompt:
    '依据正交、四分之三与机械微距参考图，重建深墨绿泪滴油箱、棕色绗缝座垫、黑色双摇篮车架、并列双缸发动机、金色倒立前叉、钢丝辐条轮组与右侧双出排气。',
  seed: 900,
  features: ['泪滴形油箱', '并列双缸', '右侧双出排气'],
  referenceTitle: '造型研究',
  referenceCount: '',
  referenceLabels: [],
  referenceCopy: '',
  sourceLinks: [],
  metrics: [
    { label: '观察视角', value: '5 种' },
    { label: '部件查看', value: '可高亮' },
    { label: '机械动作', value: '可调节' },
    { label: '拆解展示', value: '可调节' },
  ],
  footer: '本页面为原创摩托车造型研究，用于展示车辆结构、材质与互动拆解。',
  detailViewLabel: '机械细节',
};

const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
const activeProduct =
  normalizedPath === '/motorcycle' ? motorcyclePage : watchPage;
const isGallery = normalizedPath === '/';
const isMotorcycleDetail = activeProduct.path === '/motorcycle/';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('Missing #app.');

const galleryMarkup = `
  <main class="gallery-shell">
    <header class="gallery-header">
      <p class="gallery-kicker">互动模型作品集</p>
      <h1>从不同角度，观察一件作品。</h1>
      <p>选择作品进入独立详情页，旋转、缩放、查看部件，并体验拆解状态。</p>
    </header>
    <section class="work-grid" aria-label="作品列表">
      <a class="work-card work-card-watch" href="/watch/">
        <div class="work-visual">
          <img src="/generated/gallery/watch-card.jpg" alt="GW-M5610U-1 小红圈腕表主视图" />
        </div>
        <div class="work-copy">
          <span class="work-index">作品一</span>
          <h2>GW‑M5610U‑1</h2>
          <p>经典小红圈方块腕表，包含动态时间、五种视角与精细拆解。</p>
          <strong>查看腕表作品 <span>→</span></strong>
        </div>
      </a>
      <a class="work-card work-card-motorcycle" href="/motorcycle/">
        <div class="work-visual">
          <img src="/generated/gallery/motorcycle-card.jpg" alt="现代复古街车主视图" />
        </div>
        <div class="work-copy">
          <span class="work-index">作品二</span>
          <h2>现代复古街车</h2>
          <p>经典轮廓与现代机械结构交融，聚焦轮组、发动机与骑行姿态。</p>
          <strong>查看摩托车作品 <span>→</span></strong>
        </div>
      </a>
    </section>
  </main>
`;

const motorcycleHudMarkup = `
  <header class="motorcycle-title-card">
    <p class="eyebrow">${activeProduct.eyebrow}</p>
    <h1 id="scene-name">${activeProduct.title}</h1>
  </header>
  <section class="motorcycle-controls-card" aria-label="模型控制">
    <div class="section-title">
      <h2>模型控制</h2>
      <div class="compact-actions">
        <span id="validation-badge" class="badge">加载中</span>
        <button id="reset-controls" class="ghost-button" type="button">复位</button>
      </div>
    </div>
    <div id="controls" class="controls"></div>
  </section>
`;

const productPanelMarkup = `
  <aside class="panel">
    <header class="product-header">
      <div class="brand-row">
        <p class="eyebrow">${activeProduct.eyebrow}</p>
        <span class="model-chip">${activeProduct.modelChip}</span>
      </div>
      <p class="brand-mark">${activeProduct.brand}</p>
      <h1 id="scene-name">${activeProduct.title}</h1>
      <p class="lede" id="scene-description"></p>
      <div class="feature-chips" aria-label="产品特点">
        ${activeProduct.features.map((feature) => `<span>${feature}</span>`).join('')}
      </div>
    </header>

    <section class="evidence-card">
      <div class="section-title">
        <h2>${activeProduct.referenceTitle}</h2>
        <span class="evidence-count">${activeProduct.referenceCount}</span>
      </div>
      <div class="evidence-strip" aria-hidden="true">
        ${activeProduct.referenceLabels.map((label) => `<span>${label}</span>`).join('')}
      </div>
      <p>${activeProduct.referenceCopy}</p>
      <div class="source-links">
        ${activeProduct.sourceLinks
          .map(
            (link) =>
              `<a href="${link.href}" target="_blank" rel="noreferrer">${link.label}</a>`,
          )
          .join('')}
      </div>
    </section>

    <section class="prompt-card">
      <span>设计说明</span>
      <p id="prompt-copy"></p>
    </section>

    <section>
      <div class="section-title">
        <h2>体验内容</h2>
        <span id="validation-badge" class="badge">检查中</span>
      </div>
      <dl id="metrics" class="metrics"></dl>
    </section>

    <section>
      <div class="section-title">
        <h2>运动与拆解</h2>
        <button id="reset-controls" class="ghost-button" type="button">复位</button>
      </div>
      <div id="controls" class="controls"></div>
    </section>

    <section class="parts-section">
      <div class="section-title">
        <h2>部件查看</h2>
        <button id="clear-selection" class="ghost-button" type="button">清除</button>
      </div>
      <div id="parts" class="parts"></div>
    </section>

    <footer>
      ${activeProduct.footer}
    </footer>
  </aside>
`;

const viewerMarkup = `
  <main class="shell${isMotorcycleDetail ? ' is-model-first' : ''}">
    <section class="viewport" aria-label="${activeProduct.ariaLabel}">
      <canvas id="scene-canvas"></canvas>
      <div class="viewport-topbar">
        <a class="back-link" href="/">← 返回作品列表</a>
        ${isMotorcycleDetail ? '' : `<span class="viewport-badge">${activeProduct.viewportBadge}</span>`}
        <span id="view-label" class="view-label">主视图 · 四分之三</span>
      </div>
      <div class="view-dock" id="view-dock" aria-label="视角切换">
        <button type="button" data-view="hero" class="is-active">主视图</button>
        <button type="button" data-view="front">正面</button>
        <button type="button" data-view="side">侧面</button>
        <button type="button" data-view="back">背面</button>
        <button type="button" data-view="macro">${activeProduct.detailViewLabel}</button>
      </div>
      ${isMotorcycleDetail ? '' : '<div class="viewport-help">拖拽旋转 · 滚轮缩放 · 点击部件查看</div>'}
      <div class="selection-card" id="selection-card" hidden>
        <span>当前部件</span>
        <strong id="selection-name"></strong>
        <small id="selection-meta"></small>
      </div>
      ${isMotorcycleDetail ? motorcycleHudMarkup : ''}
    </section>
    ${isMotorcycleDetail ? '' : productPanelMarkup}
  </main>
`;

app.innerHTML = isGallery ? galleryMarkup : viewerMarkup;
document.title = isGallery
  ? '互动模型作品集'
  : `${activeProduct.title} · 互动模型作品`;

if (!isGallery) {
const required = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Viewer DOM is missing ${selector}.`);
  return element;
};

const canvas = required<HTMLCanvasElement>('#scene-canvas');
const viewport = required<HTMLElement>('.viewport');
const metrics = document.querySelector<HTMLElement>('#metrics');
const badge = document.querySelector<HTMLElement>('#validation-badge');
const controlsRoot = required<HTMLElement>('#controls');
const partsRoot = document.querySelector<HTMLElement>('#parts');
const promptCopy = document.querySelector<HTMLElement>('#prompt-copy');
const clearSelection =
  document.querySelector<HTMLButtonElement>('#clear-selection');
const resetControls = required<HTMLButtonElement>('#reset-controls');
const viewDock = required<HTMLElement>('#view-dock');
const viewLabel = required<HTMLElement>('#view-label');
const selectionCard = required<HTMLElement>('#selection-card');
const selectionName = required<HTMLElement>('#selection-name');
const selectionMeta = required<HTMLElement>('#selection-meta');
const sceneName = required<HTMLElement>('#scene-name');
const sceneDescription =
  document.querySelector<HTMLElement>('#scene-description');

const loadSceneSpec = async (): Promise<SceneSpecV1> => {
  const parameters = new URLSearchParams(window.location.search);
  const path = parameters.get('scene');
  if (path) {
    const url = new URL(path, window.location.href);
    if (
      url.origin !== window.location.origin ||
      !url.pathname.startsWith('/generated/')
    ) {
      throw new Error('Scene URLs must be same-origin files under /generated/.');
    }
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Unable to load scene: HTTP ${response.status}`);
    }
    return (await response.json()) as SceneSpecV1;
  }
  if (parameters.get('demo') === 'fan') {
    const { createOrbitflowFanScene } = await import(
      './examples/orbitflow-fan.js'
    );
    return createOrbitflowFanScene(FAN_PROMPT, 'zh-CN', 7);
  }
  if (isMotorcycleDetail) {
    const { createModernRoadsterMotorcycleScene } = await import(
      './examples/modern-roadster-motorcycle.js'
    );
    return createModernRoadsterMotorcycleScene(
      activeProduct.prompt,
      'zh-CN',
      activeProduct.seed,
    );
  }
  const { createGwm5610uWatchScene } = await import(
    './examples/gw-m5610u-watch.js'
  );
  return createGwm5610uWatchScene(
    activeProduct.prompt,
    'zh-CN',
    activeProduct.seed,
  );
};

const renderMetric = (label: string, value: number | string): string =>
  `<div><dt>${label}</dt><dd>${value}</dd></div>`;

interface RangeBinding {
  reset(): void;
  set(value: number): void;
}

const rangeBindings: RangeBinding[] = [];
const addRangeControl = (
  label: string,
  min: number,
  max: number,
  step: number,
  value: number,
  onInput: (value: number) => void,
  format: (value: number) => string = (candidate) => candidate.toFixed(2),
  className = '',
): RangeBinding => {
  const row = document.createElement('label');
  row.className = `control-row range-control ${className}`.trim();
  const title = document.createElement('span');
  title.textContent = label;
  const output = document.createElement('output');
  output.textContent = format(value);
  const input = document.createElement('input');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  const set = (next: number): void => {
    input.value = String(next);
    output.textContent = format(next);
    onInput(next);
  };
  input.addEventListener('input', () => set(Number(input.value)));
  const heading = document.createElement('span');
  heading.className = 'control-heading';
  heading.append(title, output);
  row.append(heading, input);
  controlsRoot.append(row);
  const binding = { reset: () => set(value), set };
  rangeBindings.push(binding);
  return binding;
};

const toDegrees = (radians: number): number => (radians * 180) / Math.PI;
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

const boot = async (): Promise<void> => {
  const spec = await loadSceneSpec();
  const requestParameters = new URLSearchParams(window.location.search);
  const usesGenericRuntime =
    requestParameters.has('scene') ||
    requestParameters.get('demo') === 'fan' ||
    !isMotorcycleDetail;
  const validation = validateSceneSpec(spec);
  if (!validation.passed) {
    throw new Error(
      validation.issues
        .filter((issue) => issue.severity === 'error')
        .map((issue) => `${issue.path}: ${issue.message}`)
        .join('\n'),
    );
  }

  sceneName.textContent = spec.name;
  if (sceneDescription) sceneDescription.textContent = spec.description;
  if (promptCopy) promptCopy.textContent = spec.source.prompt;
  if (badge) {
    badge.textContent =
      validation.issues.filter((issue) => issue.severity === 'warning').length >
      0
        ? '可以体验'
        : '准备就绪';
    badge.classList.add('is-pass');
  }
  if (metrics) {
    metrics.innerHTML = activeProduct.metrics
      .map((metric) => renderMetric(metric.label, metric.value))
      .join('');
  }

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
  const pixelRatioLimit = isMotorcycleDetail ? 1.25 : 2;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, pixelRatioLimit));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = spec.render.exposure;
  renderer.shadowMap.enabled = !isMotorcycleDetail;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  viewport.dataset.renderPixelRatio = String(renderer.getPixelRatio());
  viewport.dataset.shadowMode = isMotorcycleDetail ? 'contact' : 'realtime';
  if (isMotorcycleDetail) {
    renderer.setClearColor(spec.render.background, 1);
  } else {
    renderer.setClearAlpha(0);
  }

  const scene = new THREE.Scene();
  const span = Math.max(...spec.boundsHint);
  const targetVector = new THREE.Vector3(...spec.render.camera.target);
  if (isMotorcycleDetail) {
    scene.background = new THREE.Color(spec.render.background);
    scene.environmentIntensity = 0.74;
  }
  scene.fog = new THREE.FogExp2(
    spec.render.background,
    (isMotorcycleDetail ? 0.05 : 0.4) / span,
  );

  const camera = new THREE.PerspectiveCamera(
    spec.render.camera.fov,
    1,
    Math.max(0.0005, span / 500),
    span * 30,
  );
  camera.position.fromArray(spec.render.camera.position);
  camera.lookAt(targetVector);

  const orbit = new OrbitControls(camera, canvas);
  orbit.enableDamping = true;
  orbit.dampingFactor = 0.055;
  orbit.minDistance = span * 0.32;
  orbit.maxDistance = span * 5;
  orbit.target.copy(targetVector);

  const pmrem = new THREE.PMREMGenerator(renderer);
  const room = new RoomEnvironment();
  const environment = pmrem.fromScene(room, 0.035).texture;
  scene.environment = environment;
  room.dispose();
  pmrem.dispose();

  RectAreaLightUniformsLib.init();
  scene.add(
    new THREE.HemisphereLight(
      isMotorcycleDetail ? 0xd8e2e8 : 0xe8f0f4,
      isMotorcycleDetail ? 0x120f0e : 0x171012,
      isMotorcycleDetail ? 0.3 : 0.62,
    ),
  );

  const key = new THREE.RectAreaLight(
    isMotorcycleDetail ? 0xfff3e8 : 0xf7fbff,
    isMotorcycleDetail ? 3.1 : 5.4,
    span * (isMotorcycleDetail ? 0.75 : 0.9),
    span * (isMotorcycleDetail ? 1.1 : 1.15),
  );
  if (isMotorcycleDetail) {
    key.position.set(-span * 0.72, span * 1.05, span * 0.92);
  } else {
    key.position.set(-span * 0.72, span * 0.82, span * 1.15);
  }
  key.lookAt(targetVector);
  scene.add(key);

  const fill = new THREE.RectAreaLight(
    isMotorcycleDetail ? 0xb7c9d9 : 0xb7cee8,
    isMotorcycleDetail ? 0.85 : 2.8,
    span * (isMotorcycleDetail ? 0.65 : 0.55),
    span * 0.9,
  );
  if (isMotorcycleDetail) {
    fill.position.set(span * 0.95, span * 0.35, -span * 0.55);
  } else {
    fill.position.set(span * 0.9, span * 0.25, span * 0.72);
  }
  fill.lookAt(targetVector);
  scene.add(fill);

  const rim = new THREE.RectAreaLight(
    isMotorcycleDetail ? 0xd5e0e8 : 0xa8242d,
    isMotorcycleDetail ? 1.05 : 1.35,
    span * 0.45,
    span * 0.9,
  );
  if (isMotorcycleDetail) {
    rim.position.set(-span * 0.8, span * 0.55, -span * 1.05);
  } else {
    rim.position.set(-span * 0.75, span * 0.1, -span * 0.95);
  }
  rim.lookAt(targetVector);
  scene.add(rim);

  const shadowKey = new THREE.DirectionalLight(
    isMotorcycleDetail ? 0xfff1df : 0xffffff,
    isMotorcycleDetail ? 0.95 : 1.6,
  );
  if (isMotorcycleDetail) {
    shadowKey.position.set(-span * 0.72, span * 1.5, span * 0.92);
  } else {
    shadowKey.position.set(span * 0.85, span * 1.5, span * 1.15);
  }
  shadowKey.target.position.copy(targetVector);
  shadowKey.castShadow = !isMotorcycleDetail;
  shadowKey.shadow.mapSize.set(
    isMotorcycleDetail ? 1024 : 2048,
    isMotorcycleDetail ? 1024 : 2048,
  );
  const shadowExtent = span * (isMotorcycleDetail ? 0.68 : 0.72);
  shadowKey.shadow.camera.left = -shadowExtent;
  shadowKey.shadow.camera.right = shadowExtent;
  shadowKey.shadow.camera.top = shadowExtent;
  shadowKey.shadow.camera.bottom = -shadowExtent;
  shadowKey.shadow.camera.near = span * 0.02;
  shadowKey.shadow.camera.far = span * 5;
  shadowKey.shadow.bias = isMotorcycleDetail ? -0.0001 : -0.00008;
  if (isMotorcycleDetail) shadowKey.shadow.normalBias = 0.0015;
  scene.add(shadowKey, shadowKey.target);

  const floorY =
    spec.render.camera.target[1] -
    spec.boundsHint[1] * (isMotorcycleDetail ? 0.5 : 0.51);
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(span * (isMotorcycleDetail ? 3 : 1.8), 96),
    new THREE.MeshPhysicalMaterial({
      color: spec.render.floor,
      roughness: isMotorcycleDetail ? 0.96 : 0.68,
      metalness: isMotorcycleDetail ? 0 : 0.13,
      clearcoat: isMotorcycleDetail ? 0 : 0.16,
      clearcoatRoughness: 0.48,
      envMapIntensity: 0.5,
    }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = floorY;
  floor.receiveShadow = true;
  scene.add(floor);

  let contactShadow: THREE.Mesh | undefined;
  let contactShadowTexture: THREE.CanvasTexture | undefined;
  if (isMotorcycleDetail) {
    const shadowCanvas = document.createElement('canvas');
    shadowCanvas.width = 256;
    shadowCanvas.height = 256;
    const context = shadowCanvas.getContext('2d');
    if (context) {
      const gradient = context.createRadialGradient(
        128,
        128,
        12,
        128,
        128,
        124,
      );
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.46)');
      gradient.addColorStop(0.46, 'rgba(0, 0, 0, 0.25)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      context.fillStyle = gradient;
      context.fillRect(0, 0, 256, 256);
      contactShadowTexture = new THREE.CanvasTexture(shadowCanvas);
      contactShadow = new THREE.Mesh(
        new THREE.PlaneGeometry(span * 1.2, span * 0.46),
        new THREE.MeshBasicMaterial({
          map: contactShadowTexture,
          transparent: true,
          opacity: 0.72,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      contactShadow.rotation.x = -Math.PI / 2;
      contactShadow.position.set(0, floorY + span * 0.0015, 0);
      scene.add(contactShadow);
    }
  }

  let floorRing: THREE.Mesh | undefined;
  if (!isMotorcycleDetail) {
    floorRing = new THREE.Mesh(
      new THREE.TorusGeometry(span * 0.72, span * 0.0028, 8, 96),
      new THREE.MeshBasicMaterial({
        color: 0x661820,
        transparent: true,
        opacity: 0.42,
      }),
    );
    floorRing.rotation.x = Math.PI / 2;
    floorRing.position.y = floorY + span * 0.002;
    scene.add(floorRing);
  }

  let runtime: CompiledSceneRuntime;
  if (usesGenericRuntime) {
    const { compileSceneSpec } = await import(
      './compiler/compile-scene-spec.js'
    );
    runtime = compileSceneSpec(spec);
  } else {
    const { createModernRoadsterMotorcycleVisualRuntime } = await import(
      './examples/modern-roadster-motorcycle-runtime.js'
    );
    runtime = createModernRoadsterMotorcycleVisualRuntime(spec);
  }
  const visualMetrics = runtime.root.userData.text2threeRuntime?.metrics;
  if (visualMetrics && typeof visualMetrics === 'object') {
    viewport.dataset.visualMetrics = JSON.stringify(visualMetrics);
  }
  const initialWallClock = new Date();
  runtime.syncClock(initialWallClock);
  scene.add(runtime.root);

  const actionToggleBindings: Array<{
    reset(): void;
  }> = [];
  const addActionToggle = (
    label: string,
    defaultEnabled: boolean,
    onChange: (enabled: boolean) => void,
  ): void => {
    const toggle = document.createElement('label');
    toggle.className = 'control-row toggle-control';
    const text = document.createElement('span');
    text.textContent = label;
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('aria-label', label);
    const setToggle = (enabled: boolean): void => {
      input.checked = enabled;
      onChange(enabled);
    };
    setToggle(defaultEnabled);
    input.addEventListener('change', () => setToggle(input.checked));
    toggle.append(text, input);
    controlsRoot.append(toggle);
    actionToggleBindings.push({
      reset: () => setToggle(defaultEnabled),
    });
  };

  if (isMotorcycleDetail) {
    const frontWheelAction = spec.actions.find(
      (action) => action.id === 'front-wheel-spin',
    );
    const rearWheelAction = spec.actions.find(
      (action) => action.id === 'rear-wheel-spin',
    );
    const steeringJoint = spec.joints.find(
      (joint) => joint.id === 'steering-joint',
    );
    if (!frontWheelAction || !rearWheelAction || !steeringJoint) {
      throw new Error('摩托车模型缺少轮组或转向控制。');
    }
    const wheelMotionDefault =
      frontWheelAction.defaultEnabled && rearWheelAction.defaultEnabled;
    addActionToggle('车轮转动', wheelMotionDefault, (enabled) => {
      runtime.setActionEnabled(frontWheelAction.id, enabled);
      runtime.setActionEnabled(rearWheelAction.id, enabled);
    });
    const wheelSpeedDefault =
      (frontWheelAction.defaultValue + rearWheelAction.defaultValue) / 2;
    addRangeControl(
      '车轮速度',
      Math.max(frontWheelAction.minValue, rearWheelAction.minValue),
      Math.min(frontWheelAction.maxValue, rearWheelAction.maxValue, 2.4),
      0.1,
      wheelSpeedDefault,
      (value) => {
        runtime.setActionValue(frontWheelAction.id, value);
        runtime.setActionValue(rearWheelAction.id, value);
      },
      (value) => `${value.toFixed(1)}×`,
    );
    addRangeControl(
      '前轮方向',
      toDegrees(steeringJoint.min),
      toDegrees(steeringJoint.max),
      1,
      toDegrees(steeringJoint.defaultValue),
      (value) =>
        runtime.setJointValue(steeringJoint.id, toRadians(value)),
      (value) => `${Math.round(value)}°`,
    );
  } else {
    for (const action of spec.actions) {
      addActionToggle(`${action.label} 开关`, action.defaultEnabled, (enabled) =>
        runtime.setActionEnabled(action.id, enabled),
      );
      const isDegrees = action.kind === 'oscillate';
      addRangeControl(
        action.label,
        isDegrees ? toDegrees(action.minValue) : action.minValue,
        isDegrees ? toDegrees(action.maxValue) : action.maxValue,
        isDegrees ? 1 : 0.01,
        isDegrees ? toDegrees(action.defaultValue) : action.defaultValue,
        (value) =>
          runtime.setActionValue(
            action.id,
            isDegrees ? toRadians(value) : value,
          ),
        (value) =>
          isDegrees
            ? `${Math.round(value)}°`
            : `${Math.round((value / Math.max(action.maxValue, 0.01)) * 100)}%`,
      );
    }

    const actionJointIds = new Set(
      spec.actions.map((action) => action.jointId),
    );
    const addJointControl = (joint: JointSpec): void => {
      addRangeControl(
        joint.label,
        toDegrees(joint.min),
        toDegrees(joint.max),
        1,
        toDegrees(joint.defaultValue),
        (value) => runtime.setJointValue(joint.id, toRadians(value)),
        (value) => `${Math.round(value)}°`,
        actionJointIds.has(joint.id) ? 'action-joint-control' : '',
      );
    };
    spec.joints.forEach(addJointControl);
  }

  addRangeControl(
    '爆炸拆解',
    0,
    1,
    0.01,
    0,
    (value) => runtime.setExplode(value),
    (value) => `${Math.round(value * 100)}%`,
    'explode-control',
  );

  resetControls.addEventListener('click', () => {
    actionToggleBindings.forEach((binding) => binding.reset());
    rangeBindings.forEach((binding) => binding.reset());
  });

  const selectPart = (partId?: string): void => {
    runtime.setSelectedPart(partId);
    partsRoot
      ?.querySelectorAll<HTMLButtonElement>('button[data-part-id]')
      .forEach((button) => {
        button.classList.toggle('is-active', button.dataset.partId === partId);
      });
    const part = spec.parts.find((candidate) => candidate.id === partId);
    selectionCard.hidden = !part;
    if (part) {
      selectionName.textContent = part.label;
      selectionMeta.textContent = '当前已高亮显示';
    }
  };

  if (partsRoot) {
    for (const part of spec.parts) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.partId = part.id;
      const label = document.createElement('span');
      label.textContent = part.label;
      const semantic = document.createElement('small');
      semantic.textContent = '点击查看';
      button.append(label, semantic);
      button.addEventListener('click', () => selectPart(part.id));
      partsRoot.append(button);
    }
  }
  clearSelection?.addEventListener('click', () => selectPart());

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  canvas.addEventListener('pointerup', (event) => {
    const bounds = canvas.getBoundingClientRect();
    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    const hit = raycaster
      .intersectObject(runtime.root, true)
      .find((candidate) => candidate.object.userData.partId);
    selectPart(hit?.object.userData.partId as string | undefined);
  });

  const target = new THREE.Vector3(...spec.render.camera.target);
  const watchPresets: Readonly<
    Record<
      string,
      { label: string; position: Vec3; target: Vec3; fov: number }
    >
  > = {
    hero: {
      label: '主视图 · 四分之三',
      position: [span * 0.52, span * 0.16, span * 0.9],
      target: [target.x, target.y, target.z],
      fov: 29,
    },
    front: {
      label: '正面视图',
      position: [0, target.y, span * 1.32],
      target: [target.x, target.y, target.z],
      fov: 27,
    },
    side: {
      label: '右侧视图',
      position: [span * 1.16, target.y, span * 0.04],
      target: [target.x, target.y, target.z],
      fov: 27,
    },
    back: {
      label: '背面视图',
      position: [0, target.y, -span * 1.32],
      target: [target.x, target.y, target.z],
      fov: 27,
    },
    macro: {
      label: activeProduct.detailViewLabel,
      position: [span * 0.18, span * 0.035, span * 0.52],
      target: [0, 0, 0.002],
      fov: 24,
    },
  };
  const motorcyclePresets: typeof watchPresets = {
    hero: {
      label: '主视图 · 右前三分之四',
      position: spec.render.camera.position,
      target: [target.x, target.y, target.z],
      fov: spec.render.camera.fov,
    },
    front: {
      label: '车头正面',
      position: [target.x + span * 1.5, target.y + 0.1, target.z],
      target: [target.x, target.y + 0.08, target.z],
      fov: 29,
    },
    side: {
      label: '右侧轮廓',
      position: [target.x, target.y + 0.04, target.z + span * 1.95],
      target: [target.x, target.y, target.z],
      fov: 28,
    },
    back: {
      label: '左后四分之三',
      position: [
        target.x - span * 1.25,
        target.y + span * 0.28,
        target.z - span * 1.42,
      ],
      target: [target.x, target.y + 0.04, target.z],
      fov: 29,
    },
    macro: {
      label: activeProduct.detailViewLabel,
      position: [
        target.x + span * 0.72,
        target.y + span * 0.42,
        target.z + span * 0.5,
      ],
      target: [
        target.x - span * 0.02,
        target.y - span * 0.05,
        target.z + span * 0.04,
      ],
      fov: 31,
    },
  };
  const presets = isMotorcycleDetail ? motorcyclePresets : watchPresets;
  let cameraTransition:
    | {
        startedAt: number;
        startPosition: THREE.Vector3;
        startTarget: THREE.Vector3;
        endPosition: THREE.Vector3;
        endTarget: THREE.Vector3;
        startFov: number;
        endFov: number;
      }
    | undefined;

  const setCameraView = (viewId: string, immediate = false): void => {
    const preset = presets[viewId];
    if (!preset) return;
    viewDock
      .querySelectorAll<HTMLButtonElement>('button[data-view]')
      .forEach((button) =>
        button.classList.toggle('is-active', button.dataset.view === viewId),
      );
    viewLabel.textContent = preset.label;
    const endPosition = new THREE.Vector3(...preset.position);
    const endTarget = new THREE.Vector3(...preset.target);
    if (immediate) {
      camera.position.copy(endPosition);
      orbit.target.copy(endTarget);
      camera.fov = preset.fov;
      camera.updateProjectionMatrix();
      cameraTransition = undefined;
      return;
    }
    cameraTransition = {
      startedAt: performance.now(),
      startPosition: camera.position.clone(),
      startTarget: orbit.target.clone(),
      endPosition,
      endTarget,
      startFov: camera.fov,
      endFov: preset.fov,
    };
  };
  viewDock.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>(
      'button[data-view]',
    );
    if (button?.dataset.view) setCameraView(button.dataset.view);
  });
  setCameraView('hero', true);

  const resize = (): void => {
    const width = viewport.clientWidth;
    const height = viewport.clientHeight;
    renderer.setSize(width, height, false);
    camera.aspect = width / Math.max(height, 1);
    camera.updateProjectionMatrix();
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(viewport);
  resize();

  const clock = new THREE.Clock();
  let elapsed = 0;
  let lastClockSecond = Math.floor(initialWallClock.getTime() / 1_000);
  let animationFrame = 0;
  let lastRenderedAt = 0;
  const minimumFrameInterval = isMotorcycleDetail ? 1_000 / 45 : 0;
  const animate = (now = performance.now()): void => {
    animationFrame = requestAnimationFrame(animate);
    if (now - lastRenderedAt < minimumFrameInterval) return;
    lastRenderedAt = now;
    const delta = Math.min(clock.getDelta(), 0.05);
    elapsed += delta;
    runtime.update(delta, elapsed);
    const clockSecond = Math.floor(Date.now() / 1_000);
    if (clockSecond !== lastClockSecond) {
      lastClockSecond = clockSecond;
      runtime.syncClock(new Date());
    }

    if (cameraTransition) {
      const progress = Math.min(
        1,
        (performance.now() - cameraTransition.startedAt) / 720,
      );
      const eased = 1 - Math.pow(1 - progress, 3);
      camera.position.lerpVectors(
        cameraTransition.startPosition,
        cameraTransition.endPosition,
        eased,
      );
      orbit.target.lerpVectors(
        cameraTransition.startTarget,
        cameraTransition.endTarget,
        eased,
      );
      camera.fov = THREE.MathUtils.lerp(
        cameraTransition.startFov,
        cameraTransition.endFov,
        eased,
      );
      camera.updateProjectionMatrix();
      if (progress >= 1) cameraTransition = undefined;
    }

    orbit.update();
    renderer.render(scene, camera);
  };
  animate();

  window.addEventListener(
    'beforeunload',
    () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      orbit.dispose();
      runtime.dispose();
      floor.geometry.dispose();
      (floor.material as THREE.Material).dispose();
      floorRing?.geometry.dispose();
      (floorRing?.material as THREE.Material | undefined)?.dispose();
      if (contactShadow) {
        contactShadow.geometry.dispose();
        (contactShadow.material as THREE.Material).dispose();
      }
      contactShadowTexture?.dispose();
      environment.dispose();
      renderer.dispose();
    },
    { once: true },
  );
};

boot().catch((error: unknown) => {
  if (badge) {
    badge.textContent = '加载失败';
    badge.classList.add('is-fail');
  }
  app.insertAdjacentHTML(
    'beforeend',
    `<pre class="fatal-error">${String(
      error instanceof Error ? error.stack ?? error.message : error,
    )}</pre>`,
  );
});
}
